import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, useTheme, useMediaQuery } from '../ui';

interface CollapsibleTextAreaProps {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  onPaste?: (event: React.ClipboardEvent<HTMLTextAreaElement>) => void;
  placeholder?: string;
  /** Optional guidance text shown beneath the label (only while the field is expanded). */
  helperText?: string;
  /** Always-visible sub-text shown directly beneath the label, independent of expand state. */
  subText?: string;
  /** When true, the sub-text is rendered bold. */
  subTextBold?: boolean;
  fieldPath?: string;
  name?: string;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLTextAreaElement>;
  maxLength?: number;
  /** When true, the text area starts expanded regardless of value presence. Overrides auto-expansion logic. */
  initiallyExpanded?: boolean;
  /** When false, prevents auto-expanding when value is prefilled. Defaults to true for backward compatibility. */
  autoExpandOnValue?: boolean;
  /** When true, appends a red asterisk to the label to mark the field as required. */
  required?: boolean;
  /** When true, shows the field in an error state (red border) and auto-expands it. */
  error?: boolean;
  /** Error message rendered below the textarea when `error` is true. */
  errorMessage?: string;
  /** When true, expands the textarea (without stealing focus). Used to surface the
   *  notes field as soon as it becomes mandatory, e.g. when the user edits the amount. */
  forceExpanded?: boolean;
}

const CollapsibleTextArea: React.FC<CollapsibleTextAreaProps> = ({
  label,
  value = '',
  onChange,
  onBlur,
  onKeyDown,
  onPaste,
  placeholder = 'Please provide any additional information here.',
  helperText,
  subText,
  subTextBold = false,
  fieldPath,
  name,
  disabled = false,
  inputRef,
  maxLength,
  initiallyExpanded,
  autoExpandOnValue = true,
  required = false,
  error = false,
  errorMessage,
  forceExpanded = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isExpanded, setIsExpanded] = useState(
    initiallyExpanded ?? Boolean(value && value.trim().length > 0)
  );
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Auto-expand if value is provided
  React.useEffect(() => {
    if (!autoExpandOnValue) {
      return;
    }
    if (value && value.trim().length > 0 && !isExpanded) {
      setIsExpanded(true);
    }
  }, [value, isExpanded, autoExpandOnValue]);

  // Auto-expand when the field goes into an error state so the message is visible.
  React.useEffect(() => {
    if (error && !isExpanded) {
      setIsExpanded(true);
    }
  }, [error, isExpanded]);

  // Auto-expand when the field becomes mandatory (e.g. the user edited the amount),
  // so the notes box is visible to fill in. Expand-only — never auto-collapses.
  React.useEffect(() => {
    if (forceExpanded && !isExpanded) {
      setIsExpanded(true);
    }
  }, [forceExpanded, isExpanded]);

  useEffect(() => {
    if (typeof window === 'undefined' || !fieldPath) return undefined;

    const highlightElement = (element: HTMLElement) => {
      element.classList.add('warning-highlight');
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      element.focus({ preventScroll: true });
      window.setTimeout(() => {
        element.classList.remove('warning-highlight');
      }, 2200);
    };

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{ fieldPath?: string }>;
      if (!customEvent.detail || customEvent.detail.fieldPath !== fieldPath) return;
      setIsExpanded(true);
      window.setTimeout(() => {
        const textarea = textareaRef.current;
        if (!textarea) return;
        highlightElement(textarea);
      }, 80);
    };

    window.addEventListener('iehub:focus-field', handler as EventListener);
    return () => {
      window.removeEventListener('iehub:focus-field', handler as EventListener);
    };
  }, [fieldPath]);

  return (
    <Box
      data-error={error ? 'true' : undefined}
      sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 1 : 1.5 }}
    >
      <Typography
        variant="body2"
        onClick={() => !disabled && setIsExpanded(!isExpanded)}
        sx={{
          color: disabled ? theme.palette.text.disabled : theme.palette.primary.main,
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          opacity: disabled ? 0.6 : 1,
          fontSize: isMobile ? '14px' : '16px',
          lineHeight: isMobile ? '20px' : '22px',
          '&:hover': {
            textDecoration: disabled ? 'none' : 'underline',
          },
        }}
      >
        {isExpanded ? '−' : '+'} {label}
        {required && <span style={{ color: 'red' }}>*</span>}
      </Typography>

      {subText && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: 1.5,
            fontWeight: subTextBold ? 700 : 400,
          }}
        >
          {subText}
        </Typography>
      )}

      {isExpanded && helperText && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: 1.5,
            mt: isMobile ? -0.5 : -1,
          }}
        >
          {helperText}
        </Typography>
      )}

      {isExpanded && (
        <textarea
          ref={(instance) => {
            textareaRef.current = instance;
            if (typeof inputRef === 'function') {
              inputRef(instance);
            } else if (inputRef && 'current' in (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>)) {
              (inputRef as React.MutableRefObject<HTMLTextAreaElement | null>).current = instance;
            }
          }}
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          placeholder={placeholder}
          name={name || fieldPath}
          data-field-path={fieldPath}
          disabled={disabled}
          maxLength={maxLength}
          style={{
            width: '100%',
            minHeight: '100px',
            padding: '12px',
            border: `1px solid ${error ? theme.palette.error.main : theme.palette.grey[300]}`,
            borderRadius: '4px',
            fontSize: isMobile ? '12px' : '14px',
            lineHeight: isMobile ? '16px' : '20px',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
            backgroundColor: disabled ? theme.palette.grey[50] : 'white',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.6 : 1,
          }}
        />
      )}

      {error && errorMessage && (
        <Typography
          variant="caption"
          sx={{ color: theme.palette.error.main, fontSize: isMobile ? '12px' : '13px', lineHeight: 1.4 }}
        >
          {errorMessage}
        </Typography>
      )}
    </Box>
  );
};

export default CollapsibleTextArea;
